import { useId, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { QuestionKebabMenu } from '@/features/content/questionActions/QuestionKebabMenu';
import { MathText } from '@/features/content/MathText';
import { formatTolerancePercent } from '@/features/questions/numericalTolerance';
import type {
  NormalizedContentUnit,
  RawComponent,
  RawVariable,
  TextUnitData,
} from '@/features/content/useComponent';
import {
  describeVariableValues,
  evaluateVariableBasedAnswer,
} from '@/features/questions/randomVariables';
import { ExactAnswerQuestion } from '@/features/questions/ExactAnswerQuestion';
import { HintTabContent } from '@/features/questions/HintTabContent';
import { MultipleChoiceQuestion } from '@/features/questions/MultipleChoiceQuestion';
import { NumericalQuestion } from '@/features/questions/NumericalQuestion';
import { QuestionContent } from '@/features/questions/QuestionContent';
import { QuestionInfoPanel } from '@/features/questions/QuestionInfoPanel';
import { SelectAllQuestion } from '@/features/questions/SelectAllQuestion';
import { SolutionTabContent } from '@/features/questions/SolutionTabContent';
import { getInfoPanelTabs } from '@/features/questions/useInfoPanelTabs';
import type { QuestionAIState } from '@/features/ai-homework/aiHomeworkTypes';
import {
  normalizeQuestionSections,
  type NormalizedDisclosureData,
  type NormalizedQuestionData,
} from '@/features/questions/questionNormalizer';

import type {
  EditableQuestion,
  EditableQuestionImage,
  EditableQuestionPart,
} from './editorTypes';
import { buildPromptPreviewUnits } from './promptContentUnits';
import {
  buildRepresentativeSubstitute,
  identitySubstitute,
  type SubstituteFn,
} from './representativeVariablePreview';
import { STRUCTURAL_EDIT_LOCK_REASON } from './questionEditRestrictions';
import {
  cleanQuestionForType,
  describeFormulaError,
  getQuestionTypeLabel,
  getRubricTotalScore,
} from './questionDialogUtils';
import { serializeQuestionSetPayload } from './questionSerializerEditor';
import styles from './QuestionPreviewCard.module.css';

interface QuestionPreviewCardProps {
  actionDisabled?: boolean;
  actionDisabledReason?: string;
  canReorder?: boolean;
  disabled?: boolean;
  displayLabel?: string;
  displayNumber: number;
  isPractice?: boolean;
  isSelected?: boolean;
  onCopy: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onSelectMultiple: () => void;
  onToggleSelected?: () => void;
  question: EditableQuestion;
  selectionActive?: boolean;
  structuralEditsRestricted?: boolean;
}

interface QuestionReadonlyPreviewProps {
  displayLabel?: string;
  displayNumber?: number;
  isPractice?: boolean;
  question: EditableQuestion;
}

function getPointsLabel(points: number) {
  return `${points} point${points === 1 ? '' : 's'}`;
}

// The editor's own fallback, for a question the page's reading skips.
function getDraftPoints(value: number | undefined) {
  return value !== undefined && value > 0 ? value : 1;
}

function formatRubricPoints(points: number) {
  return `${points} ${points === 1 ? 'pt' : 'pts'}`;
}

const PREVIEW_SECTION_ID = 'question-preview';

/**
 * The draft as the chapter page will read it once it is saved: through the
 * editor's own serializer, then the normalizer the page uses, for an
 * instructor. What the preview then draws from it, its options and their
 * marks, its answer fields and its points, cannot drift from the page.
 */
function readAsChapterPage(question: EditableQuestion): NormalizedQuestionData | undefined {
  const [rawQuestion] = serializeQuestionSetPayload(
    { preLectureQuestions: [], questions: [cleanQuestionForType(question)] },
    '',
    '',
  ).question;
  const component: RawComponent = {
    sections: [{ id: PREVIEW_SECTION_ID, question_section: { questions: [rawQuestion] } }],
  };

  return normalizeQuestionSections(component, { isInstructorRequest: true })[0]?.questions[0];
}

// The dialog's preview sits over the chapter page, which draws this same
// question with the same ids. Its controls get ids of their own, or their
// labels would point at the page's.
function withPreviewIds(
  question: NormalizedQuestionData,
  prefix: string,
): NormalizedQuestionData {
  const id = `${prefix}${question.id}`;

  return question.kind === 'multiPart'
    ? { ...question, id, parts: question.parts.map((part) => withPreviewIds(part, prefix)) }
    : { ...question, id };
}

function refuseSubmission(): Promise<void> {
  return Promise.reject(new Error('A preview saves nothing.'));
}

/**
 * A question's options or answer field, drawn by the components the chapter
 * page draws them with for an instructor: answers marked as the page marks
 * them, and no Save or Check.
 */
function PageQuestionBody({ question }: { question: NormalizedQuestionData | undefined }) {
  if (!question) {
    return null;
  }

  const shared = {
    areSolutionsVisible: true,
    isSectionClosed: false,
    isSectionOpen: true,
    onCheck: refuseSubmission,
    onSave: refuseSubmission,
    pending: false,
    role: 'instructor' as const,
    showActions: false,
  };

  if (question.kind === 'multipleChoice') {
    return <MultipleChoiceQuestion {...shared} question={question} />;
  }

  if (question.kind === 'selectAll') {
    return <SelectAllQuestion {...shared} question={question} />;
  }

  if (question.kind === 'numerical') {
    return <NumericalQuestion {...shared} question={question} />;
  }

  if (question.kind === 'exactAnswer') {
    return <ExactAnswerQuestion {...shared} question={question} />;
  }

  return null;
}

function getTextUnit(
  id: string,
  html: string | undefined,
): NormalizedContentUnit[] {
  const normalizedHtml = html?.trim() || '<p></p>';

  return [
    {
      caption: undefined,
      collapseByDefault: false,
      disableAutoNumbering: true,
      heading: undefined,
      html: normalizedHtml,
      id,
      kind: 'text',
      numberedHeading: undefined,
      summaryLabel: 'Question content',
    } satisfies TextUnitData,
  ];
}

// Capital letters, as students see their parts: "Part A", "Part B".
function getPartLabel(index: number) {
  return String.fromCharCode('A'.charCodeAt(0) + index);
}

// Preview units for a prompt: the edited text plus the prompt images, so images
// render in edit mode and reflect edits/deletions live. Falls back to a lone
// text unit when there is nothing renderable.
function getPromptUnits(
  id: string,
  images: EditableQuestionImage[] | undefined,
  html: string | undefined,
): NormalizedContentUnit[] {
  const nextHtml = html?.trim() || '<p></p>';
  const units = buildPromptPreviewUnits(nextHtml, images ?? []);
  return units.length > 0 ? units : getTextUnit(id, nextHtml);
}

function noop() {}

// A preview never calls the AI, so the hint tab is handed a permanently idle
// state and told not to offer the button.
const PREVIEW_IDLE_AI_HINT = {
  error: null,
  isLoading: false,
  isRegenerating: false,
  response: null,
  statusMessage: null,
  vote: { error: null, isVoting: false, userVote: null },
} satisfies QuestionAIState['hint'];

function buildPreviewDisclosure(
  id: string,
  summaryLabel: string,
  html: string | undefined,
  images: EditableQuestionImage[] | undefined,
): NormalizedDisclosureData | undefined {
  const hasText = Boolean(html?.replace(/<[^>]*>/g, '').trim());

  if (!hasText && (images ?? []).length === 0) {
    return undefined;
  }

  return {
    // The instructor is previewing their own authoring, so it is always shown;
    // student-side gating (attempts, due dates) is not what this preview is for.
    isVisible: true,
    summaryLabel,
    units: getPromptUnits(id, images, html),
  };
}

/**
 * The answer a student's numbers would actually produce, shown beside the
 * formula.
 *
 * A formula is only correct if the evaluator can read it, and the two are easy
 * to confuse: "arccos(Fx/F)*(180/pi())" is right as maths and unreadable to
 * mathjs, which spells it acos and treats pi as a constant. Showing the computed
 * value turns that from an invisible grading outage into something an instructor
 * sees the moment they look at the question — and when it cannot be computed,
 * the reason is here rather than nowhere.
 */
function renderComputedAnswer(
  question: EditableQuestion | EditableQuestionPart,
  representativeValues: RawVariable[],
) {
  const formula = question.variableBasedAnswer?.trim();

  if (!formula || representativeValues.length === 0) {
    return null;
  }

  const { error, value } = evaluateVariableBasedAnswer(formula, representativeValues);

  if (error) {
    return (
      <p className={styles.answerFormulaError} role="alert">
        Cannot compute an answer: {describeFormulaError(formula, error)}
      </p>
    );
  }

  return (
    <p className={styles.answerComputed}>
      = {value} for {describeVariableValues(representativeValues)}
    </p>
  );
}

function renderAnswerSummary(
  question: EditableQuestion | EditableQuestionPart,
  representativeValues: RawVariable[] = [],
) {
  if (question.type === 'SELECT_ALL') {
    // Every marked option, so an instructor can see at a glance what a student
    // must select in full to earn credit.
    const markedIndices = [...(question.correctAnswerIndices ?? [])].sort(
      (a, b) => a - b,
    );

    return (
      <div className={styles.answerSummary}>
        <span className={styles.answerLabel}>Correct answers</span>
        {markedIndices.length > 0 ? (
          <span>
            {markedIndices.map((index, position) => (
              <span key={`correct-${index}`}>
                {position > 0 ? ', ' : ''}
                {`Option ${index + 1}`}
                {question.options[index] ? (
                  <>
                    {': '}
                    <MathText text={question.options[index]} />
                  </>
                ) : null}
              </span>
            ))}
          </span>
        ) : (
          <span>None marked</span>
        )}
      </div>
    );
  }

  if (question.type === 'MULTIPLE_CHOICE') {
    const correctAnswerIndex = question.correctAnswerIndex;
    const correctOption =
      correctAnswerIndex !== undefined ? question.options[correctAnswerIndex] : undefined;

    return (
      <div className={styles.answerSummary}>
        <span className={styles.answerLabel}>Correct answer</span>
        {correctAnswerIndex !== undefined ? (
          <span>
            Option {correctAnswerIndex + 1}
            {correctOption ? (
              <>
                {': '}
                <MathText text={correctOption} />
              </>
            ) : null}
          </span>
        ) : (
          <span>Not selected</span>
        )}
      </div>
    );
  }

  if (question.type === 'NUMERICAL_WITH_ERROR') {
    const value =
      question.variableBasedAnswer?.trim() ||
      (question.numericalAnswer !== undefined
        ? String(question.numericalAnswer)
        : 'Not set');

    return (
      <div className={styles.answerSummary}>
        <span className={styles.answerLabel}>Correct answer</span>
        <span>
          <MathText text={value} />
          {question.answerUnits ? (
            <>
              {' '}
              <MathText text={question.answerUnits} />
            </>
          ) : null}
          {question.numericalError !== undefined ? (
            <span className={styles.answerDetail}>
              {' '}
              tolerance {formatTolerancePercent(question.numericalError)}
            </span>
          ) : null}
          {renderComputedAnswer(question, representativeValues)}
        </span>
      </div>
    );
  }

  if (question.type === 'EXACT_ANSWER') {
    const value = question.variableBasedAnswer?.trim() || question.exactAnswer || 'Not set';

    return (
      <div className={styles.answerSummary}>
        <span className={styles.answerLabel}>Correct answer</span>
        <span>
          <MathText text={value} />
          {question.answerUnits ? (
            <>
              {' '}
              <MathText text={question.answerUnits} />
            </>
          ) : null}
        </span>
      </div>
    );
  }

  return null;
}

/**
 * The hint and solution as a student meets them.
 *
 * Deliberately built from the SAME pieces the question renderer uses —
 * `getInfoPanelTabs` decides which tabs exist, `QuestionInfoPanel` draws them,
 * and `HintTabContent`/`SolutionTabContent` fill them. The preview previously
 * hand-rolled a pair of `<details>` disclosures, which is how it came to look
 * nothing like the thing it was previewing. There is no second implementation
 * left to drift.
 */
function PreviewDisclosures({
  question,
  substitute = identitySubstitute,
}: {
  question: EditableQuestion | EditableQuestionPart;
  substitute?: SubstituteFn;
}) {
  const hintDisclosure = buildPreviewDisclosure(
    `${question.id}-hint`,
    'Hint',
    question.hintHtml ? substitute(question.hintHtml) : undefined,
    question.hintImages,
  );
  const solutionDisclosure = buildPreviewDisclosure(
    `${question.id}-solution`,
    'Solution',
    question.detailedAnswerHtml
      ? substitute(question.detailedAnswerHtml)
      : undefined,
    question.detailedAnswerImages,
  );
  const tabs = getInfoPanelTabs({ hintDisclosure, solutionDisclosure });

  if (tabs.length === 0) {
    return null;
  }

  return (
    <QuestionInfoPanel
      ariaLabel="Question help preview"
      renderContent={(tabId) =>
        tabId === 'hint' ? (
          <HintTabContent
            aiHint={PREVIEW_IDLE_AI_HINT}
            builtInHint={hintDisclosure}
            onRequestAIHint={noop}
            onVoteAIHint={noop}
            // A preview has no AI: the button would offer something it cannot do.
            showAIHintButton={false}
          />
        ) : (
          <SolutionTabContent solution={solutionDisclosure} />
        )
      }
      tabs={tabs}
    />
  );
}

function renderRubricSummary(question: EditableQuestion) {
  const rubric = question.gradingRubric;

  if (question.type !== 'AI_UPLOAD' || !rubric) {
    return null;
  }

  const totalScore = getRubricTotalScore(rubric);

  return (
    <section className={styles.rubricPreview} aria-label="AI Upload grading rubric">
      <div className={styles.uploadNote}>
        Students will upload their work to be graded by KAI.
      </div>
      <div className={styles.rubricHeader}>
        <h4 className={styles.rubricTitle}>Grading rubric</h4>
        <span className={styles.rubricTotal}>{formatRubricPoints(totalScore)}</span>
      </div>
      <ol className={styles.rubricList}>
        {rubric.rubricUnits.map((unit) => (
          <li className={styles.rubricItem} key={unit.id}>
            <div className={styles.rubricItemHeader}>
              <span className={styles.rubricName}>{unit.name || 'Untitled unit'}</span>
              <span className={styles.rubricPoints}>{formatRubricPoints(unit.score)}</span>
            </div>
            {unit.details ? (
              <p className={styles.rubricDetails}>{unit.details}</p>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

function QuestionPartPreview({
  index,
  pagePart,
  part,
  representativeValues = [],
  substitute = identitySubstitute,
}: {
  index: number;
  // The part as the chapter page reads it.
  pagePart?: NormalizedQuestionData;
  part: EditableQuestionPart;
  // Parts share the parent's variables, so the parent resolves them once.
  representativeValues?: RawVariable[];
  // Multi-part questions share the parent's variables, so the parent passes its
  // representative-value substitution down to each part.
  substitute?: SubstituteFn;
}) {
  const label = getPartLabel(index);

  return (
    <li className={styles.partPreview}>
      <div className={styles.partHeader}>
        <span className={styles.partTitle}>Part {label}</span>
        <span className={styles.typeBadge}>{getQuestionTypeLabel(part.type)}</span>
        <span className={styles.points}>
          {getPointsLabel(pagePart?.pointsPossible ?? getDraftPoints(part.value))}
        </span>
      </div>
      <QuestionContent
        headingLevel={4}
        role="instructor"
        units={getPromptUnits(
          `${part.id}-prompt`,
          part.promptImages,
          substitute(part.contentHtml),
        )}
      />
      <PageQuestionBody question={pagePart} />
      {renderAnswerSummary(part, representativeValues)}
      <PreviewDisclosures question={part} substitute={substitute} />
    </li>
  );
}

export function QuestionReadonlyPreview({
  displayLabel,
  displayNumber,
  isPractice = false,
  question,
}: QuestionReadonlyPreviewProps) {
  const titleLabel =
    displayLabel ??
    (displayNumber !== undefined ? `Question ${displayNumber}` : 'Question preview');
  const { hasRepresentativeValues, substitute, values } =
    buildRepresentativeSubstitute(question);
  const previewIdPrefix = useId();
  const pageQuestion = useMemo(() => {
    if (question.type === 'UNSUPPORTED') {
      return undefined;
    }

    try {
      const read = readAsChapterPage(question);

      return read ? withPreviewIds(read, previewIdPrefix) : undefined;
    } catch {
      // A draft half-way through being written can be one the page cannot
      // read yet. The preview then shows what the editor knows, and never
      // takes the dialog, and the instructor's unsaved work, down with it.
      return undefined;
    }
  }, [previewIdPrefix, question]);

  return (
    <article className={styles.previewCard}>
      <header className={styles.previewHeader}>
        <div className={styles.previewTitleGroup}>
          <p className={styles.eyebrow}>{titleLabel}</p>
          <div className={styles.previewMeta}>
            <span className={styles.typeBadge}>{getQuestionTypeLabel(question.type)}</span>
            {isPractice ? <span className={styles.practiceBadge}>Practice</span> : null}
          </div>
        </div>
        <span className={styles.points}>
          {getPointsLabel(pageQuestion?.pointsPossible ?? getDraftPoints(question.value))}
        </span>
      </header>

      {question.type === 'UNSUPPORTED' ? (
        <p className={styles.unsupported}>
          This question type is preserved but cannot be previewed in the new editor yet.
        </p>
      ) : (
        <>
          <QuestionContent
            headingLevel={4}
            role="instructor"
            units={getPromptUnits(
              `${question.id}-prompt`,
              question.promptImages,
              substitute(question.contentHtml),
            )}
          />

          {hasRepresentativeValues ? (
            <p className={styles.representativeValuesNote} role="note">
              Representative values shown. Each student sees their own randomized
              numbers.
            </p>
          ) : null}

          {question.imagePaths && question.imagePaths.length > 0 ? (
            <div className={styles.imageAttachments}>
              {question.imagePaths.map((imagePath, index) => (
                <img
                  alt={`Question image ${index + 1}`}
                  className={styles.imageAttachment}
                  key={`${imagePath}-${index}`}
                  src={imagePath}
                />
              ))}
            </div>
          ) : null}

          {question.type === 'MULTI_PART' ? (
            <ol className={styles.parts}>
              {(question.parts ?? []).map((part, index) => (
                <QuestionPartPreview
                  index={index}
                  key={part.id}
                  pagePart={
                    pageQuestion?.kind === 'multiPart' ? pageQuestion.parts[index] : undefined
                  }
                  part={part}
                  representativeValues={values}
                  substitute={substitute}
                />
              ))}
            </ol>
          ) : question.type === 'AI_UPLOAD' ? (
            <>
              {renderRubricSummary(question)}
              <PreviewDisclosures question={question} substitute={substitute} />
            </>
          ) : (
            <>
              <PageQuestionBody question={pageQuestion} />
              {renderAnswerSummary(question, values)}
              <PreviewDisclosures question={question} substitute={substitute} />
            </>
          )}
        </>
      )}
    </article>
  );
}

export function QuestionPreviewCard({
  actionDisabled = false,
  actionDisabledReason = 'Save your changes first.',
  canReorder = true,
  disabled = false,
  displayLabel,
  displayNumber,
  isSelected = false,
  isPractice = false,
  onCopy,
  onDelete,
  onEdit,
  onSelectMultiple,
  onToggleSelected,
  question,
  selectionActive = false,
  structuralEditsRestricted = false,
}: QuestionPreviewCardProps) {
  const structuralDisabled = disabled || structuralEditsRestricted;
  const canDrag = !structuralDisabled && canReorder && !selectionActive;
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    disabled: !canDrag,
    id: question.id,
  });
  const actionLabel = displayLabel?.toLowerCase() ?? `question ${displayNumber}`;

  return (
    <li
      className={styles.sortableItem}
      data-dragging={isDragging ? 'true' : 'false'}
      data-invalid={question.validationError ? 'true' : 'false'}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <div className={styles.editorShell}>
        <div className={styles.controlRail}>
          {selectionActive ? (
            <label className={styles.selectionLabel}>
              <input
                aria-label={`Select ${actionLabel}`}
                checked={isSelected}
                className={styles.selectionCheckbox}
                onChange={onToggleSelected}
                type="checkbox"
              />
            </label>
          ) : canDrag ? (
            <Tooltip content={`Reorder ${actionLabel}`}>
              <button
                {...attributes}
                {...listeners}
                aria-label={`Reorder ${actionLabel}`}
                className={styles.dragHandle}
                type="button"
              >
                <GripVertical aria-hidden="true" size={18} strokeWidth={2} />
              </button>
            </Tooltip>
          ) : null}
        </div>

        <div className={styles.cardColumn}>
          <div className={styles.cardActions}>
            <Button
              aria-label={`Edit ${actionLabel}`}
              className={styles.iconButton}
              iconOnly
              onClick={onEdit}
              size="small"
              variant="ghost"
            >
              <Pencil aria-hidden="true" size={18} strokeWidth={2} />
            </Button>
            {!structuralDisabled && !selectionActive ? (
              <QuestionKebabMenu
                disabled={actionDisabled || question.type === 'UNSUPPORTED'}
                disabledReason={
                  structuralEditsRestricted
                    ? STRUCTURAL_EDIT_LOCK_REASON
                    : actionDisabledReason
                }
                onCopy={onCopy}
                onDelete={onDelete}
                onSelectMultiple={onSelectMultiple}
                questionLabel={actionLabel}
              />
            ) : null}
          </div>
          <QuestionReadonlyPreview
            displayLabel={displayLabel}
            displayNumber={displayNumber}
            isPractice={isPractice}
            question={question}
          />
          {question.validationError ? (
            <p className={styles.errorMessage} role="alert">
              {question.validationError}
            </p>
          ) : null}
        </div>
      </div>
    </li>
  );
}
